import $http from '@vue-util/http';
import { toRaw } from 'vue';
import { fireTag } from '@vue-util/tagManager';
import getTag from './analyticTagConstants';
import getPageSpecificValues from './GoogleAdTagConstants';
import { onElementWillBeVisible } from './visabilityHelper';

const analyticsUtil = {
  fireBeacon(beacon) {
    if (beacon.length) {
      $http.get(beacon);
      return true;
    }
    return false;
  },
  fireAnalyticTag(monetizationComponent, device, page, event, position) {
    const analyticData = {
      monetizationComponent,
      event,
      position,
    };
    const analyticTag = getTag(analyticData, device, page);
    fireTag('link', analyticTag);
  },
  getSMData(sponsoredProducts, productId) {
    return toRaw(sponsoredProducts).find((n) => n.product.id === productId)
      .product.meta.productSMData;
  },
  fireOnLoadBeacons(sponsoredProducts, carouselLoadBeacon, banner) {
    if (carouselLoadBeacon) this.fireBeacon(carouselLoadBeacon);
    if (banner) {
      const onLoadBeacon = banner.onLoadBeacon || '';
      /* istanbul ignore next */
      if (onLoadBeacon) this.fireBeacon(banner.onLoadBeacon);
    }
    toRaw(sponsoredProducts).forEach((n) => {
      const onLoadBeacon = n.product.meta.productSMData.onLoadBeacon || '';
      if (onLoadBeacon) this.fireBeacon(onLoadBeacon);
    });
  },
  fireOnViewBeacons(productsInView, sponsoredProducts, carouselViewBeacon, banner) {
    const onViewBeacons = [];
    if (carouselViewBeacon) onViewBeacons.push(carouselViewBeacon);
    if (banner) onViewBeacons.push(banner.onViewBeacon);
    productsInView.forEach((product) => {
      const onViewBeacon = this.getSMData(sponsoredProducts, product.productId).onViewBeacon || '';
      if (onViewBeacon) onViewBeacons.push(onViewBeacon);
    });
    if (onViewBeacons && onViewBeacons.length > 0) {
      onViewBeacons.forEach((beacon) => this.fireBeacon(beacon));
    }
  },
  bannerClickEvent(device, page, banner, brandShowcaseClickTagEnabled) {
    const { onClickBeacon = '' } = banner;
    if (onClickBeacon) this.fireBeacon(onClickBeacon);
    if (brandShowcaseClickTagEnabled) {
      this.fireAnalyticTag(
        'brand showcase',
        device,
        page,
        'click',
        'brandshowcaseimage',
      );
    }
  },
  carouselClickEvent({
    productId,
    position,
    sponsoredProducts,
    device,
    page,
    isBrandShowcase,
    clickTagEnabled,
    clickAnalyticsFired,
  }) {
    if (clickAnalyticsFired.indexOf(productId) !== -1) {
      return;
    }
    const { onClickBeacon = '' } = this.getSMData(sponsoredProducts, productId);
    if (onClickBeacon) this.fireBeacon(onClickBeacon);
    if (clickTagEnabled) {
      if (isBrandShowcase) {
        this.fireAnalyticTag(
          'brand showcase',
          device,
          page,
          'click',
          `brandshowcase${position}`,
        );
      } else {
        this.fireAnalyticTag('sponsored items', device, page, 'click');
      }
    }
    clickAnalyticsFired.push(productId);
  },
  carouselScrollEvent(viewAnalyticsFired, productsInView, sponsoredProducts) {
    if (viewAnalyticsFired.indexOf(productsInView[0].productId) !== -1) {
      return;
    }
    productsInView.forEach((product) => {
      viewAnalyticsFired.push(product.productId);
    });
    this.fireOnViewBeacons(productsInView, sponsoredProducts);
  },
  arrowClickEvent(page, arrowDir) {
    fireTag('link', {
      custom_link_name: `${page === 'product' ? 'pdp' : page
        } sponsored carousel ${arrowDir} click`,
      event_name: `${page === 'product' ? 'pdp' : page
        } sponsored carousel ${arrowDir} click`,
    });
  },
  onLoadEvent({
    selector,
    viewAnalyticsFired,
    productsInView,
    sponsoredProducts,
    device,
    page,
    banner,
    viewTagEnabled,
    carouselBeacons,
  }) {
    this.fireOnLoadBeacons(sponsoredProducts, carouselBeacons.onLoadBeacon, banner);
    const target = document.querySelector(selector);
    productsInView.forEach((product) => {
      viewAnalyticsFired.push(product.productId);
    });
    const options = {
      threshold: 0.5,
    };
    const observer = new IntersectionObserver(
      (entries, intersectionObserver) => {
        entries.forEach((entry) => {
          /* istanbul ignore next */
          if (entry.isIntersecting) {
            if (viewTagEnabled) {
              /* istanbul ignore next */
              if (selector === '.brand-showcase' && banner) {
                this.fireAnalyticTag(
                  'brand showcase',
                  device,
                  page,
                  'view',
                  'brandshowcase',
                );
              } else {
                this.fireAnalyticTag(
                  'sponsored items',
                  device,
                  page,
                  'view',
                  productsInView.length,
                );
              }
            }
            this.fireOnViewBeacons(productsInView, sponsoredProducts, carouselBeacons.onViewBeacon, banner);
            intersectionObserver.disconnect();
          }
        });
      },
      options,
    );
    observer.observe(target);
  },
  initGAMClickTag(id, device, page, brand) {
    window.addEventListener('blur', () => {
      const activeElementId = document.activeElement.id || false;
      if (
        activeElementId
        && activeElementId.indexOf('google_ads_iframe') !== -1
      ) {
        const adData = getPageSpecificValues(page).ad_data[brand];
        const position = adData[device][id].pos;
        const analyticsDevice = device === 'mweb' ? 'mobile' : device;
        const analyticsPage = page === 'pdp' ? 'product' : page;
        const analyticsPosition = position.replace(page, '').replace('_', '');
        const slotPositionSubString = `/${position}/`;
        const isAdClicked = activeElementId.indexOf(slotPositionSubString) !== -1;
        if (isAdClicked) this.fireAnalyticTag('google ad', analyticsDevice, analyticsPage, 'click', analyticsPosition);
      }
    });
  },
  initGAMImpressionTag(id, device, page, brand) {
    const adData = getPageSpecificValues(page).ad_data[brand];
    const currentAd = adData[device][id] || '';
    const analyticsDevice = device === 'mweb' ? 'mobile' : device;
    const analyticsPage = page === 'pdp' ? 'product' : page;
    const analyticsPosition = currentAd.pos.replace(page, '').replace('_', '');
    const target = document.querySelector(`#${id}`);
    if (currentAd && target) {
      const options = {
        threshold: 0.5,
      };
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && target.clientHeight > 20) {
              this.fireAnalyticTag(
                'google ad',
                analyticsDevice,
                analyticsPage,
                'view',
                analyticsPosition,
              );
              observer.disconnect();
            }
          });
        },
        options,
      );
      observer.observe(target);
    }
  },
  handleArrowClick(arrowDir, isRightArrowClicked, isLeftArrowClicked, pageType) {
    if (!isRightArrowClicked.value && arrowDir === 'right') {
      this.arrowClickEvent(pageType, arrowDir);
      isRightArrowClicked.value = true;
    } else if (!isLeftArrowClicked.value && arrowDir === 'left') {
      this.arrowClickEvent(pageType, arrowDir);
      isLeftArrowClicked.value = true;
    }
  },
  initInGridSponsoredItemsAnalytics(formatLevelBeacons, enableImpressionTagsForSMProducts, enableClickTagsForSMProducts,
    isPhone, isSearch, sponsoredItemRefs) {
    const { onLoadBeacon: formatLoadBeacon = '', onViewBeacon: formatViewBeacon = '', onClickBeacon: formatClickBeacon = '' } = formatLevelBeacons;
    const selector = '.sortable-grid-sponsored-items';
    const items = Array.from(document.querySelectorAll(selector));
    const rootMargin = 0;
    const percentVisible = 50;

    if (enableImpressionTagsForSMProducts) {
      const fireViewTag = () => {
        fireTag('link', {
          event_name: 'sponsored items displayed',
        });
      };
      onElementWillBeVisible(
        items[0],
        fireViewTag,
        rootMargin,
        percentVisible,
      );
    }
    let formatClickBeaconFired = false;
    let formatViewBeaconFired = false;

    let observerCleanups = [];
    if (sponsoredItemRefs) {
      observerCleanups = Object.keys(sponsoredItemRefs).map((key) => {
        const el = sponsoredItemRefs[key].value;

        let stop = () => {};
        const observer = new IntersectionObserver(([{ isIntersecting }]) => {
          /* istanbul ignore next */
          if (isIntersecting) {
            this.fireBeacon(el.getAttribute('data-view-beacon'));
            const viewPentaleapBeacon = el.getAttribute('data-view-pentaleap-beacon') || false;
            if (viewPentaleapBeacon) this.fireBeacon(viewPentaleapBeacon);
            /* istanbul ignore next */
            if (!formatViewBeaconFired) {
              this.fireBeacon(formatViewBeacon);
              formatViewBeaconFired = true;
            }

            stop();
          }
        }, {
          rootMargin: `${rootMargin}px`,
          threshold: percentVisible / 100,
        });

        observer.observe(el);

        stop = () => {
          observer.disconnect();
        };

        return () => {
          stop();
        };
      });
    }

    const clickCleanups = items.map((item) => {
      const data = item.dataset;

      const onClick = () => {
        /* istanbul ignore next */
        if (!formatClickBeaconFired) {
          this.fireBeacon(formatClickBeacon);
          formatClickBeaconFired = true;
        }
        this.fireBeacon(data.clickBeacon);
        if (data.clickPentaleapBeacon) this.fireBeacon(data.clickPentaleapBeacon);
        /* istanbul ignore next */
        if (enableClickTagsForSMProducts) {
          const utagData = {
            event_name: 'sponsored items click',
            product_id: data.productId,
            product_name: data.productName,
            item_position: data.liindex,
            sponsored_revenue: 0,
            product_finding_method: isSearch ? 'sponsored:search' : 'sponsored:browse',
          };
          fireTag('link', utagData);
        }
      };

      item.firstChild.addEventListener('click', onClick, { once: true });
      this.fireBeacon(data.loadBeacon || '');

      return () => {
        item.firstChild.removeEventListener('click', onClick);
      };
    });
    this.fireBeacon(formatLoadBeacon);

    return () => {
      [...observerCleanups, ...clickCleanups].forEach((fn) => fn());
    };
  },
  sponsoredItemTopLoadEvent(selector, productSMData) {
    const { onLoadBeacon = '', onViewBeacon = '' } = productSMData;
    const target = document.querySelector(selector);
    const rootMargin = 0;
    const percentVisible = 50;
    const cb = () => {
      this.fireBeacon(onViewBeacon);
    };
    onElementWillBeVisible(target, cb, rootMargin, percentVisible);
    this.fireBeacon(onLoadBeacon);
  },
  sponsoredItemTopClickEvent(clickBeacon, enableClickTagsForSMProducts) {
    this.fireBeacon(clickBeacon);
    if (enableClickTagsForSMProducts) {
      const analyticData = { monetizationComponent: 'sponsored item top' };
      const analyticTag = getTag(analyticData);
      fireTag('link', analyticTag);
    }
  },
};

export default analyticsUtil;
